import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  CaretRight,
  CaretLeft,
  House,
  MapPin,
  X,
  ArrowRight,
  FileDoc,
  DeviceMobile,
  CheckCircle,
  SignOut,
} from "@phosphor-icons/react";

const SUGGESTED_ROOMS = [
  "Living Room",
  "Primary Bedroom",
  "Kitchen",
  "Office",
  "Garage",
  "Basement",
  "Attic",
  "Storage",
  "Outdoor / Garden",
];

export default function Onboarding() {
  const { user, updateProfile, logout, refreshHouseholds, setActiveLedger, households } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState(user?.name || "");
  const [householdName, setHouseholdName] = useState(user?.household_name || "");
  const [address, setAddress] = useState(user?.address || "");
  const [rooms, setRooms] = useState(user?.rooms || []);
  const [customRoom, setCustomRoom] = useState("");

  const toggleRoom = (r) => {
    setRooms((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  };

  const addCustomRoom = () => {
    const r = customRoom.trim();
    if (!r) return;
    if (!rooms.includes(r)) setRooms([...rooms, r]);
    setCustomRoom("");
  };

  const removeRoom = (r) => setRooms(rooms.filter((x) => x !== r));

  const goNext = async () => {
    if (step === 1) {
      if (!name.trim() || !householdName.trim()) {
        toast.error("Please fill in both fields.");
        return;
      }
      setSaving(true);
      const res = await updateProfile({ name: name.trim(), household_name: householdName.trim() });
      if (!res.ok) {
        setSaving(false);
        toast.error(res.error);
        return;
      }
      // v3.15 — auto-create a real Household on the first onboarding
      // step (if the user doesn't already have one) so they never land
      // on the now-hidden Personal ledger. The household is auto-seeded
      // with the DEFAULT_HOUSEHOLD_ROOMS backend-side; step 2 rooms are
      // added on top of that.
      try {
        if (!households || households.length === 0) {
          const created = await api.post("/households", { name: householdName.trim() });
          await refreshHouseholds();
          setActiveLedger(created.data.id);
        }
      } catch (err) {
        // Non-fatal — the user can create a household manually from
        // the Households page later. Log and continue.
        console.warn("Household auto-create failed:", err?.response?.data?.detail || err?.message);
      }
      setSaving(false);
      setStep(2);
    } else if (step === 2) {
      setSaving(true);
      const res = await updateProfile({ address: address.trim() || null, rooms });
      // Also mirror onboarding rooms into the newly-created household
      // so the RoomPicker sees them immediately alongside the defaults.
      try {
        const activeHousehold = (households || []).find(
          (h) => h.name === householdName.trim(),
        );
        if (activeHousehold && rooms.length > 0) {
          await Promise.all(
            rooms.map((r) =>
              api.post(`/households/${activeHousehold.id}/rooms`, { name: r }).catch(() => null),
            ),
          );
        }
      } catch {
        // Silent by design. Room seeding from onboarding is best-effort:
        // the household defaults (Kitchen, Living Room, ...) are already
        // written server-side on household creation, so if these extra
        // onboarding rooms fail to POST (transient network, permission
        // race, whatever), the user still has a fully usable room
        // vocabulary — the missing ones can be added later from the
        // Rooms panel. Surfacing a toast here would confuse users at the
        // exact moment they're being onboarded.
      }
      setSaving(false);
      if (!res.ok) {
        toast.error(res.error);
        return;
      }
      setStep(3);
    }
  };

  const finish = async (path) => {
    setSaving(true);
    await updateProfile({ onboarding_completed: true });
    setSaving(false);
    if (path) navigate(path, { replace: true });
    else navigate("/", { replace: true });
  };

  const skipAll = async () => {
    setSaving(true);
    const res = await updateProfile({ onboarding_completed: true });
    setSaving(false);
    if (!res.ok) {
      toast.error(res.error || "Could not finish setup. Try again.");
      return;
    }
    navigate("/", { replace: true });
  };

  const handleSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen ledger-paper flex flex-col" data-testid="onboarding-page">
      {/* Header */}
      <header className="px-6 md:px-10 py-6 border-b border-[#C9C4B8] flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-3 pointer-events-none">
          <img
            src="/logo-mark.png"
            alt="The Asset Arbor"
            className="w-10 h-10 object-contain shrink-0"
            draggable="false"
          />
          <div className="leading-none">
            <div className="font-serif-display text-2xl text-[#0A192F]">The Asset Arbor</div>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
              First-Run Setup
            </div>
          </div>
        </Link>
        <button
          onClick={handleSignOut}
          data-testid="onboarding-signout"
          className="text-xs text-[#5C5F58] hover:text-[#0A192F] inline-flex items-center gap-1.5"
        >
          <SignOut size={12} /> Sign out
        </button>
      </header>

      {/* Stepper */}
      <div className="px-6 md:px-10 py-6 border-b border-[#E0DDD6] bg-[#F9F8F6]/60">
        <div className="max-w-2xl mx-auto flex items-center gap-3" data-testid="stepper">
          <Step n={1} label="Identity" active={step === 1} done={step > 1} />
          <span className="h-px flex-1 bg-[#C9C4B8]" />
          <Step n={2} label="Household" active={step === 2} done={step > 2} />
          <span className="h-px flex-1 bg-[#C9C4B8]" />
          <Step n={3} label="Begin" active={step === 3} />
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 px-6 md:px-10 py-10 md:py-16">
        <div className="max-w-2xl mx-auto">
          {step === 1 && (
            <section data-testid="step-1" className="space-y-6 fade-in-up">
              <div>
                <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                  Step 1 of 3 · Identity
                </div>
                <h1 className="font-serif-display text-4xl md:text-5xl text-[#0A192F] leading-[1.05]">
                  Welcome.<br />Let&apos;s open the ledger.
                </h1>
                <p className="text-sm text-[#5C5F58] mt-4 max-w-lg leading-relaxed">
                  This is your standing record — a permanent, time-stamped manifest of physical wealth.
                  We&apos;ll need a name on the cover and a name for the estate it describes.
                </p>
              </div>

              <div className="bg-white border border-[#E0DDD6] p-6 space-y-4">
                <Field label="Your name">
                  <input
                    data-testid="onboarding-name"
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Eleanor Whitfield"
                    className="w-full bg-white border border-[#E0DDD6] px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
                  />
                </Field>
                <Field label="Household / Estate name">
                  <input
                    data-testid="onboarding-household"
                    value={householdName}
                    onChange={(e) => setHouseholdName(e.target.value)}
                    placeholder="e.g., The Whitfield Estate · 14 Maple Lane"
                    className="w-full bg-white border border-[#E0DDD6] px-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
                  />
                  <p className="text-xs text-[#5C5F58] mt-2 italic">
                    This appears at the top of your dashboard and on the Estate Manifest PDF.
                  </p>
                </Field>
              </div>

              <NavButtons
                onNext={goNext}
                nextLabel="Continue"
                nextDisabled={saving || !name.trim() || !householdName.trim()}
                saving={saving}
                hideBack
                onSkip={skipAll}
              />
            </section>
          )}

          {step === 2 && (
            <section data-testid="step-2" className="space-y-6 fade-in-up">
              <div>
                <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                  Step 2 of 3 · Household
                </div>
                <h1 className="font-serif-display text-4xl md:text-5xl text-[#0A192F] leading-[1.05]">
                  The property.
                </h1>
                <p className="text-sm text-[#5C5F58] mt-4 max-w-lg leading-relaxed">
                  Tag the rooms you&apos;ll be cataloguing. We&apos;ll suggest these whenever you start a Walkthrough.
                  Add or remove anytime.
                </p>
              </div>

              <div className="bg-white border border-[#E0DDD6] p-6 space-y-5">
                <Field label="Address (optional)">
                  <div className="relative">
                    <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F58]" />
                    <input
                      data-testid="onboarding-address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="e.g., 14 Maple Lane, Brookline MA"
                      className="w-full bg-white border border-[#E0DDD6] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#1A3622]"
                    />
                  </div>
                  <p className="text-xs text-[#5C5F58] mt-2 italic">Used only on the Estate Manifest PDF.</p>
                </Field>

                <div>
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                    Suggested rooms — toggle any
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid="suggested-rooms">
                    {SUGGESTED_ROOMS.map((r) => {
                      const on = rooms.includes(r);
                      return (
                        <button
                          key={r}
                          onClick={() => toggleRoom(r)}
                          data-testid={`suggested-${r}`}
                          className={`px-3 py-1.5 text-xs border transition-colors ${
                            on
                              ? "bg-[#1A3622] text-[#F9F8F6] border-[#1A3622]"
                              : "bg-white text-[#0A192F] border-[#E0DDD6] hover:border-[#1A3622]"
                          }`}
                        >
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                    Add a custom room
                  </div>
                  <div className="flex gap-2">
                    <input
                      data-testid="onboarding-custom-room"
                      value={customRoom}
                      onChange={(e) => setCustomRoom(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomRoom(); } }}
                      placeholder="e.g., Wine cellar, Workshop"
                      className="flex-1 bg-white border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]"
                    />
                    <button
                      onClick={addCustomRoom}
                      data-testid="add-room"
                      className="px-4 py-2 text-sm bg-[#0A192F] text-[#F9F8F6] hover:bg-[#0F2818]"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {rooms.length > 0 && (
                  <div>
                    <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                      Your room list · {rooms.length}
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="selected-rooms">
                      {rooms.map((r) => (
                        <span key={r} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#F9F8F6] border border-[#E0DDD6] text-[#0A192F]">
                          <House size={11} /> {r}
                          <button onClick={() => removeRoom(r)} aria-label={`Remove ${r}`} className="text-[#5C5F58] hover:text-[#6B1718]">
                            <X size={11} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <NavButtons
                onBack={() => setStep(1)}
                onNext={goNext}
                nextLabel="Continue"
                nextDisabled={saving}
                saving={saving}
                onSkip={skipAll}
              />
            </section>
          )}

          {step === 3 && (
            <section data-testid="step-3" className="space-y-6 fade-in-up">
              <div>
                <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                  Step 3 of 3 · Begin
                </div>
                <h1 className="font-serif-display text-4xl md:text-5xl text-[#0A192F] leading-[1.05]">
                  Solve the cold start.
                </h1>
                <p className="text-sm text-[#5C5F58] mt-4 max-w-lg leading-relaxed">
                  Pick your first move. You can always switch later — both paths lead to the same ledger.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#E0DDD6] bg-white">
                <button
                  onClick={() => finish("/walkthrough")}
                  disabled={saving}
                  data-testid="begin-walkthrough"
                  className="text-left p-6 md:p-7 border-b md:border-b-0 md:border-r border-[#E0DDD6] hover:bg-[#F9F8F6]/60 transition-colors group disabled:opacity-60"
                >
                  <div className="w-12 h-12 bg-[#1A3622] flex items-center justify-center mb-4">
                    <DeviceMobile size={24} weight="regular" color="#F9F8F6" />
                  </div>
                  <h3 className="font-serif-display text-2xl text-[#0A192F] leading-tight">
                    Start a Walkthrough.
                  </h3>
                  <p className="text-sm text-[#5C5F58] mt-2 leading-relaxed">
                    Walk room by room. Narrate or photograph each item. Best on a phone.
                  </p>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs text-[#1A3622] font-medium group-hover:gap-2 transition-all">
                    Begin <ArrowRight size={12} weight="bold" />
                  </div>
                </button>

                <button
                  onClick={() => finish("/assets/new")}
                  disabled={saving}
                  data-testid="begin-import"
                  className="text-left p-6 md:p-7 hover:bg-[#F9F8F6]/60 transition-colors group disabled:opacity-60"
                >
                  <div className="w-12 h-12 bg-[#0A192F] flex items-center justify-center mb-4">
                    <FileDoc size={24} weight="regular" color="#F9F8F6" />
                  </div>
                  <h3 className="font-serif-display text-2xl text-[#0A192F] leading-tight">
                    Import an inventory.
                  </h3>
                  <p className="text-sm text-[#5C5F58] mt-2 leading-relaxed">
                    Have an existing inventory in TXT, DOCX, or PDF? Upload it; we&apos;ll parse every line.
                  </p>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs text-[#1A3622] font-medium group-hover:gap-2 transition-all">
                    Upload <ArrowRight size={12} weight="bold" />
                  </div>
                </button>
              </div>

              <div className="flex justify-between items-center pt-4">
                <button onClick={() => setStep(2)} className="text-sm text-[#5C5F58] inline-flex items-center gap-2 hover:text-[#0A192F]" data-testid="back-step3">
                  <CaretLeft size={14} /> Back
                </button>
                <button
                  onClick={skipAll}
                  data-testid="skip-onboarding"
                  className="text-xs text-[#5C5F58] underline underline-offset-4 hover:text-[#0A192F]"
                >
                  Skip — explore the empty ledger
                </button>
              </div>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}

function Step({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-2" data-testid={`step-${n}-indicator`}>
      <div
        className={`w-7 h-7 flex items-center justify-center font-mono-ledger text-xs border ${
          done
            ? "bg-[#1A3622] text-[#F9F8F6] border-[#1A3622]"
            : active
            ? "bg-white text-[#0A192F] border-[#1A3622]"
            : "bg-white text-[#5C5F58] border-[#E0DDD6]"
        }`}
      >
        {done ? <CheckCircle size={14} weight="fill" /> : n}
      </div>
      <span className={`text-xs font-mono-ledger uppercase tracking-[0.2em] ${active || done ? "text-[#0A192F]" : "text-[#5C5F58]"}`}>
        {label}
      </span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function NavButtons({ onBack, onNext, nextLabel, nextDisabled, saving, hideBack, onSkip }) {
  return (
    <div className="flex items-center justify-between pt-4">
      <div className="flex gap-3">
        {!hideBack && (
          <button
            onClick={onBack}
            disabled={saving}
            data-testid="back-btn"
            className="text-sm text-[#5C5F58] inline-flex items-center gap-2 hover:text-[#0A192F] disabled:opacity-60"
          >
            <CaretLeft size={14} /> Back
          </button>
        )}
        {onSkip && (
          <button
            onClick={onSkip}
            disabled={saving}
            data-testid="skip-btn"
            className="text-xs text-[#5C5F58] underline underline-offset-4 hover:text-[#0A192F] disabled:opacity-60"
          >
            Skip setup
          </button>
        )}
      </div>
      <button
        onClick={onNext}
        disabled={nextDisabled || saving}
        data-testid="next-btn"
        className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm font-medium border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-60"
      >
        {saving ? "Saving…" : nextLabel} <CaretRight size={14} weight="bold" />
      </button>
    </div>
  );
}
